import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightIcon,
  CheckIcon,
  CopyIcon,
  Loader2,
  Inbox,
  CheckCircle2,
  // RefreshCwIcon,
  RotateCcw,
} from 'lucide-react';
import {
  DashboardWrapper,
  DashboardContainer,
  PageHeader,
  HeaderLeft,
  TitleRow,
  Title,
  Description,
  HeaderRight,
  ClientCodePill,
  ClientCodeLabel,
  ClientCodeValue,
  // PillDivider,
  IconButton,
  // RegenerateButton,
  MetricsGrid,
  MetricCard,
  MetricHeader,
  MetricLabelGroup,
  MetricIcon,
  MetricLabel,
  PulseDot,
  MetricValueRow,
  MetricValue,
  MetricFooter,
  MetricFooterText,
  MetricArrow,
  PulseCount,
} from './dashboard.styles';
import { useClientDashboardStats } from './hooks/useClientDashboard';
import { EmptyState, RetryButton, SpinWrapper } from '../../../settings/appStyle/global.style';
// import { useAuth } from '../../../hooks/useAuth';

const buildMetrics = (stats) => [
  // {
  //   id: 'total',
  //   label: 'Total tickets',
  //   value: stats.totalTickets ?? stats.closedTickets + stats.openTickets,
  //   path: '/tickets',
  // },
  {
    id: 'open',
    label: 'Active tickets',
    pulseCount: stats.unviewedQueries > 0 ? stats.unviewedQueries : 0,
    value: stats.openTickets,
    icon: Inbox,
    footerText: 'View active tickets',
    path: '/tickets',
    query: { status: 'OPEN' },
  },
  {
    id: 'closed',
    label: 'Resolved tickets',
    value: stats.closedTickets,
    icon: CheckCircle2,
    footerText: 'View resolved tickets',
    path: '/tickets',
    query: { status: 'CLOSED' },
  },
  // {
  //   id: 'team',
  //   label: 'Team members',
  //   value: stats.totalUsers,
  //   path: '/team-members',
  // },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  // const { userData } = useAuth();

  const { data, isLoading, isError, refetch, isFetching } = useClientDashboardStats();

  // const regenerateCodeMutation = useRegenerateClientCode({
  //   onError: (err) => {
  //     window.alert(
  //       `Failed to regenerate client code${err?.message ? `: ${err.message}` : '. Please try again.'}`
  //     );
  //   },
  // });

  const handleNavigate = (metric) => {
    const qs = metric.query ? `?${new URLSearchParams(metric.query).toString()}` : '';
    navigate(`${metric.path}${qs}`);
  };

  const handleCopy = async (clientCode) => {
    if (!clientCode) return;
    try {
      await navigator.clipboard.writeText(clientCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      window.alert('Could not copy to clipboard. Please copy it manually.');
    }
  };

  // const handleRegenerate = () => {
  //   const confirmed = window.confirm(
  //     'Regenerate your client code? The current code will stop working immediately.'
  //   );
  //   if (!confirmed) return;
  //   regenerateCodeMutation.mutate();
  // };

  const renderClientCodePill = (clientCode) => {
    if (!clientCode) return null;

    return (
      <ClientCodePill>
        <ClientCodeLabel>Client code:</ClientCodeLabel>
        <ClientCodeValue>{clientCode}</ClientCodeValue>
        <IconButton
          type="button"
          onClick={() => handleCopy(clientCode)}
          title="Copy code"
          aria-label="Copy client code"
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
        </IconButton>

        {/* {userData?.rootUser && userData?.rootUser === 1 ? (
          <>
            <PillDivider />
            <RegenerateButton
              type="button"
              onClick={handleRegenerate}
              disabled={regenerateCodeMutation.isPending}
              title="Regenerate client code"
            >
              <RefreshCwIcon data-spin={regenerateCodeMutation.isPending ? 'true' : undefined} />
              {regenerateCodeMutation.isPending ? 'Regenerating…' : 'Regenerate'}
            </RegenerateButton>
          </>
        ) : null} */}
      </ClientCodePill>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <EmptyState>
          <SpinWrapper>
            <Loader2 />
          </SpinWrapper>
          <p>Loading dashboard statistics…</p>
        </EmptyState>
      );
    }

    if (isError) {
      return (
        <EmptyState>
          <p>Failed to load dashboard statistics</p>
          <p>Please try again later</p>
          <RetryButton type="button" onClick={() => refetch()} disabled={isFetching}>
            <RotateCcw />
            {isFetching ? 'Retrying…' : 'Retry'}
          </RetryButton>
        </EmptyState>
      );
    }

    const stats = data?.statistics;

    if (!stats) {
      return (
        <EmptyState>
          <p>No statistics available</p>
        </EmptyState>
      );
    }

    return (
      <MetricsGrid>
        {buildMetrics(stats).map((metric) => {
          const Icon = metric.icon;

          return (
            <MetricCard key={metric.id} onClick={() => handleNavigate(metric)}>
              <MetricHeader>
                <MetricLabelGroup>
                  <MetricIcon aria-hidden="true">
                    <Icon />
                  </MetricIcon>
                  <MetricLabel>{metric.label}</MetricLabel>
                </MetricLabelGroup>

                {metric.pulseCount > 0 && (
                  <PulseDot
                    role="status"
                    aria-label={`${metric.pulseCount} unviewed`}
                    title="Unviewed"
                  >
                    <PulseCount>{metric.pulseCount > 99 ? '99+' : metric.pulseCount}</PulseCount>
                  </PulseDot>
                )}
              </MetricHeader>

              <MetricValueRow>
                <MetricValue>{metric.value}</MetricValue>
              </MetricValueRow>

              <MetricFooter>
                <MetricFooterText>{metric.footerText}</MetricFooterText>
                <MetricArrow>
                  <ArrowRightIcon />
                </MetricArrow>
              </MetricFooter>
            </MetricCard>
          );
        })}
      </MetricsGrid>
    );
  };

  return (
    <DashboardWrapper>
      <DashboardContainer>
        <PageHeader>
          <HeaderLeft>
            <TitleRow>
              <Title>Dashboard</Title>
            </TitleRow>
            <Description>Support workspace overview and customer queue telemetry</Description>
          </HeaderLeft>

          {!isLoading && !isError && data?.statistics?.clientCode && (
            <HeaderRight>{renderClientCodePill(data?.statistics?.clientCode)}</HeaderRight>
          )}
        </PageHeader>

        {renderContent()}
      </DashboardContainer>
    </DashboardWrapper>
  );
};

export default Dashboard;
